import React from 'react';
import JourneyModal from './JourneyModal';
import JourneyList, { JourneyActions, JourneyFoot, sectionRows } from './JourneyList';
import { formatMinutes, getOptionalSuggestions } from '../components/applicationProgress';

/**
 * Shown after "Save & exit": confirms progress is saved and what's left,
 * then lets the talent keep working or log out. Copy differs for the
 * application (pre-approval) and the profile (post-approval).
 */
const SaveExitModal = ({ open, user, progress, onKeepWorking, onLogout, onDashboard }) => {
    if (!user || !progress) return null;

    const isProfile = !!user.isApplicationApproved?.();
    const remaining = progress.requiredRemaining;
    const timeLeft = progress.minutesLeft ? ` · about ${formatMinutes(progress.minutesLeft)} left` : '';
    const inReview = isProfile && !!(user.isPendingProfileApproved?.() || user.isProfilePendingAppeal?.());
    const notLive = isProfile && !user.isProfileApproved?.() && !inReview;

    // Nothing required left on the profile: suggest the optional sections instead
    const optional = isProfile && !remaining.length ? getOptionalSuggestions(user).remaining : [];

    return (
        <JourneyModal
            open={open}
            onClose={onKeepWorking}
            icon="check"
            title={isProfile ? 'Your profile is saved' : 'Your application is saved'}
            subtitle={isProfile
                ? `${progress.requiredDoneCount} of ${progress.requiredCount} required sections done${timeLeft}`
                : `${progress.requiredDoneCount} of ${progress.requiredCount} sections done${timeLeft}`}
        >
            <p className="m-0 text-center text-sm text-gray-600">
                {inReview
                    ? 'Wripple is reviewing your profile — changes you make are included.'
                    : notLive
                        ? (remaining.length
                            ? "Clients can't see you until you submit and Wripple approves."
                            : "Everything required is done — submit for approval when you're ready.")
                        : isProfile
                            ? 'Your changes are saved.'
                            : 'Pick up exactly where you left off next time you log in.'}
            </p>

            {remaining.length ? (
                <JourneyList
                    title="Left to do"
                    meta={progress.minutesLeft ? `~${formatMinutes(progress.minutesLeft)}` : null}
                    rows={sectionRows(remaining)}
                />
            ) : (
                <JourneyList title="Optional — worth adding" rows={sectionRows(optional)} />
            )}

            <JourneyActions>
                {/* Log out comes first so phones stack "Keep working" on top; desktop shows it second */}
                <button type="button" className="btn btn-outline-dark px-6 sm:order-last" onClick={onLogout}>Log out</button>
                <button type="button" className="btn btn-primary px-6" onClick={onKeepWorking}>Keep working</button>
            </JourneyActions>

            <JourneyFoot>
                {isProfile ? (
                    <>Or <button type="button" onClick={onDashboard} className="bg-transparent border-0 p-0 text-gray-600 underline">go to your dashboard</button></>
                ) : (
                    'Once you submit, Wripple reviews applications within 24–72 hours.'
                )}
            </JourneyFoot>
        </JourneyModal>
    );
};

export default SaveExitModal;
